import React, { useContext, useId } from 'react'
import { useIntl } from 'react-intl'
import { NativeSlider } from '@notino/react-styleguide'

// types
import { CategoriesResponse, SalonsListPageQuery } from '../../../../types/types'

// utils
import { PAGE_LINKS } from '../../../../utils/helper'
import { getTopLevelCategoryChangeEvent } from '../../../../utils/dataLayerEvents'
import { MyLocationContext } from '../../../../utils/myLocationProvider'
import { pushToDataLayer } from '../../../../utils/dataLayer'
import { BREAKPOINTS_INTS } from '../../../../styles/constants'

// hooks
import useMessages from '../../../../hooks/useMessages'

// components
import VisuallyHidden from '../../../../atoms/VisuallyHidden/VisuallyHidden'

// styles
import * as SC from './CategoriesNavigationStyles'

type Props = {
	categoriesData: CategoriesResponse
	query: SalonsListPageQuery
}

// Matches notino.cz homepage shop categories: equal-width slides, gap 0, content centered in the cell
const DESKTOP_SLIDES_TO_SHOW = 8

// component
const CategoriesNavigation = (props: Props) => {
	const { categoriesData, query } = props
	const { locale } = useIntl()
	const { messages } = useMessages()
	const { myLocation } = useContext(MyLocationContext)
	const headingId = useId()

	const onCategoryButtonClick = (categoryID: string, categorySlug?: string) => {
		// push to dataLayer
		const event = getTopLevelCategoryChangeEvent({ categoryID, categorySlug })
		pushToDataLayer(event)
	}

	return (
		<SC.TopLevelCategoriesSliderWrapper aria-labelledby={headingId}>
			<VisuallyHidden as='h3' id={headingId}>
				{messages.Categories}
			</VisuallyHidden>
			<NativeSlider
				slidesToShow={DESKTOP_SLIDES_TO_SHOW}
				slidesToScroll={DESKTOP_SLIDES_TO_SHOW}
				gap={'0px'}
				prevArrow={'chevronInCircle'}
				nextArrow={'chevronInCircle'}
				responsive={[
					{
						breakpoint: BREAKPOINTS_INTS.lg,
						settings: { slidesToShow: 5, slidesToScroll: 5, gap: '0px' }
					},
					{
						breakpoint: BREAKPOINTS_INTS.sm,
						settings: { slidesToShow: 3, slidesToScroll: 3, gap: '0px' }
					},
					{
						breakpoint: BREAKPOINTS_INTS.xs,
						settings: { slidesToShow: 2, slidesToScroll: 2, gap: '0px' }
					}
				]}
			>
				{categoriesData.categories.map((category) => (
					<SC.TopLevelCategorySlide key={category.id}>
						<SC.TopLevelCategoryLink
							href={PAGE_LINKS['/salons/all/:categorySlug'](locale, category.seoSlug || category.id, {
								...myLocation,
								...query,
								page: 1
							})}
							onClick={() => onCategoryButtonClick(category.id, category.seoSlug || category.id)}
						>
							<SC.TopLevelCategoryImgWrapper>
								<SC.TopLevelCategoryImg src={category.image?.resizedImages.thumbnail} alt='' width={80} height={80} />
							</SC.TopLevelCategoryImgWrapper>
							<SC.TopLevelCategoryTitle>{category.name}</SC.TopLevelCategoryTitle>
						</SC.TopLevelCategoryLink>
					</SC.TopLevelCategorySlide>
				))}
			</NativeSlider>
		</SC.TopLevelCategoriesSliderWrapper>
	)
}

export default CategoriesNavigation
